import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { RequestHMSupport } from '@/components/shared/RequestHMSupport';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  GraduationCap, BookOpen, Award, Activity, Loader2,
  Download, Plus, Crown, Wrench, MessagesSquare, ShieldCheck,
  AlertTriangle, Pencil, ChevronRight,
} from 'lucide-react';
import { SuiteHero } from '@/components/client/SuiteHero';
import { GuidedEmptyState } from '@/components/client/GuidedEmptyState';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import TalentTrainShell from '@/pages/talenttrain/TalentTrainShell';
import { TtCertAlerts } from '@/components/client/TtCertAlerts';
import RoleMatrixDialog from '@/components/client/RoleMatrixDialog';
import { SuiteImportWizard } from '@/components/client/SuiteImportWizard';

const initials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

function KpiCard({ icon: Icon, label, value, hint, accent = 'primary', testid }) {
  const accents = {
    primary: 'from-primary/15 to-primary/5 text-primary',
    success: 'from-success/15 to-success/5 text-success',
    warning: 'from-warning/15 to-warning/5 text-warning',
    info: 'from-info/15 to-info/5 text-info',
  };
  return (
    <Card className="p-4 shadow-card hover:shadow-elevated transition-smooth" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${accents[accent]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium truncate">{label}</p>
          <p className="text-xl font-bold tabular-nums mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
    </Card>
  );
}

// Founder 2026-08 — consultative showcase detail (no enrollment, no plan creation).
const ROUTE_CODES = ['leadership', 'technical', 'softskills', 'compliance'];

function RouteDetailDialog({ code, onClose }) {
  const { t } = useTranslation();
  // Guard — only known route codes may render (never expose raw i18n keys).
  if (!code || !ROUTE_CODES.includes(code)) return null;
  const bullets = t(`clientTalentTrain.routes.${code}Bullets`, { returnObjects: true });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="ctt-route-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{t(`clientTalentTrain.routes.${code}`)}</DialogTitle>
          <DialogDescription className="text-[15px] pt-1">
            {t('clientTalentTrain.routes.detailIntro')}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-[15px] list-disc pl-5" data-testid="ctt-route-detail-bullets">
          {(Array.isArray(bullets) ? bullets : []).map((b) => <li key={b}>{b}</li>)}
        </ul>
        <div className="rounded-lg bg-primary/5 border border-primary/15 p-4 mt-2" data-testid="ctt-route-detail-cta">
          <p className="font-semibold text-[15px]">{t('clientTalentTrain.routes.ctaQuestion')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('clientTalentTrain.routes.ctaContact')}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ROUTE_DEFS = [
  { code: 'leadership', icon: Crown },
  { code: 'technical',  icon: Wrench },
  { code: 'softskills', icon: MessagesSquare },
  { code: 'compliance', icon: ShieldCheck },
];

// ===========================================================================
// Training Guide by Role tab (reference-only)
// ===========================================================================

// Founder 2026-08 — read-only disclosure: inspect the full guide without Edit.
function GuideViewDialog({ row, onClose }) {
  const { t } = useTranslation();
  if (!row) return null;
  const cats = [
    ['mandatory', t('clientTalentTrain.matrix.th.mandatory')],
    ['optional', t('clientTalentTrain.matrix.th.optional')],
    ['recerts', t('clientTalentTrain.matrix.th.recerts')],
    ['internal', t('clientTalentTrain.matrix.th.internal')],
    ['external', t('clientTalentTrain.matrix.th.external')],
  ];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="ctt-guide-view-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{row.position}</DialogTitle>
          <DialogDescription className="text-sm">
            {t('clientTalentTrain.matrix.viewHint')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {cats.map(([key, label]) => (
            <div key={key} data-testid={`ctt-guide-view-${key}`}>
              <p className="text-[13px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                {label} ({(row[key] || []).length})
              </p>
              {(row[key] || []).length === 0 ? (
                <p className="text-sm text-muted-foreground italic">—</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {(row[key] || []).map((item) => (
                    <Badge key={item} variant="outline" className="text-sm font-normal">{item}</Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
function MatrixTab({ canEdit }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const matrixData = await clientApi.ttGetRoleMatrix();
      setItems(matrixData?.items || []);
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditingRow(row); setDialogOpen(true); };

  if (loading) {
    return (
      <div className="py-10 text-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      <Card className="p-5 shadow-card" data-testid="ctt-matrix-card">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t('clientTalentTrain.matrix.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('clientTalentTrain.matrix.subtitle')}</p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={openCreate} data-testid="ctt-matrix-add">
              <Plus className="h-4 w-4 mr-1.5" /> {t('clientTalentTrain.matrix.addPosition')}
            </Button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t('clientTalentTrain.matrix.empty')}
          </p>
        ) : (
          <Table className="text-[15px]">
            <TableHeader>
              <TableRow>
                <TableHead>{t('clientTalentTrain.matrix.th.position')}</TableHead>
                <TableHead>{t('clientTalentTrain.matrix.th.mandatory')}</TableHead>
                <TableHead className="text-center">{t('clientTalentTrain.matrix.th.optional')}</TableHead>
                <TableHead className="text-center">{t('clientTalentTrain.matrix.th.recerts')}</TableHead>
                <TableHead className="text-center">{t('clientTalentTrain.matrix.th.internal')}</TableHead>
                <TableHead className="text-center">{t('clientTalentTrain.matrix.th.external')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row, i) => (
                <TableRow key={row.id || i} data-testid={`ctt-matrix-row-${i}`}>
                  <TableCell className="font-medium">{row.position}</TableCell>
                  <TableCell className="text-sm">
                    {(row.mandatory || []).slice(0, 3).map((m) => (
                      <Badge key={m} variant="outline" className="text-sm font-normal mr-1 mb-1">{m}</Badge>
                    ))}
                    {(row.mandatory || []).length > 3 && (
                      <span className="text-xs text-muted-foreground">+{(row.mandatory || []).length - 3}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground tabular-nums">
                    {(row.optional || []).length}
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground tabular-nums">
                    {(row.recerts || []).length}
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground tabular-nums">
                    {(row.internal || []).length}
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground tabular-nums">
                    {(row.external || []).length}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setViewRow(row)}
                            data-testid={`ctt-matrix-view-${i}`}>
                      <Eye className="h-4 w-4 mr-1" /> {t('clientTalentTrain.matrix.view')}
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(row)}
                              data-testid={`ctt-matrix-edit-${i}`}>
                        <Pencil className="h-4 w-4 mr-1" /> {t('clientTalentTrain.matrix.edit')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <GuideViewDialog row={viewRow} onClose={() => setViewRow(null)} />
      <RoleMatrixDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        row={editingRow}
        onSaved={load}
      />
    </>
  );
}

// ===========================================================================
// Resumen (existing UI) tab content
// ===========================================================================
function SummaryTab({ users, navigate, canEdit }) {
  const [unreadMap, setUnreadMap] = useState({});
  const [alerts, setAlerts] = useState({ items: [], count: 0 });
  const [openRoute, setOpenRoute] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  useEffect(() => {
    if (canEdit) {
      clientApi.ttDiscussionUnread().then(setUnreadMap).catch(() => {});
      clientApi.ttTrainingAlerts().then((a) => setAlerts(a || { items: [], count: 0 })).catch(() => {});
    }
  }, [canEdit]);
  const { t, i18n } = useTranslation();

  const totals = useMemo(() => {
    let total = 0, planned = 0, inProgress = 0, completed = 0;
    let withPlan = 0;
    for (const r of users) {
      const a = r.action_counts || {};
      total += a.total || 0;
      planned += a.planned || 0;
      inProgress += a.in_progress || 0;
      completed += a.completed || 0;
      if ((a.total || 0) > 0) withPlan += 1;
    }
    const completion = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, planned, inProgress, completed, withPlan, completion };
  }, [users]);

  const byDept = useMemo(() => {
    const m = new Map();
    for (const r of users) {
      const dept = r.department_name || '—';
      if (!m.has(dept)) m.set(dept, { dept, total: 0, withActions: 0 });
      const e = m.get(dept);
      e.total += 1;
      if ((r.action_counts?.total || 0) > 0) e.withActions += 1;
    }
    return [...m.values()]
      .map((d) => ({ ...d, pct: d.total > 0 ? Math.round((d.withActions / d.total) * 100) : 0 }))
      .sort((a, b) => b.pct - a.pct);
  }, [users]);

  const sortedUsers = useMemo(() => [...users].sort((a, b) =>
    (b.action_counts?.total || 0) - (a.action_counts?.total || 0),
  ), [users]);

  return (
    <div className="space-y-5">
      {/* Recertification alerts (sourced from training actions) —
          Founder final cleanup batch §11: actionable drill-down detail. */}
      {canEdit && alerts.count > 0 && (
        <Card className="p-4 shadow-card bg-gradient-to-br from-warning/5 to-transparent"
              data-testid="ctt-alerts-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-semibold text-sm">{t('clientTalentTrain.alerts.title')}</h3>
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                        onClick={() => setAlertsOpen((v) => !v)} data-testid="ctt-alerts-toggle">
                  {alertsOpen
                    ? t('clientTalentTrain.alerts.hideDetail', { defaultValue: 'Ocultar detalle' })
                    : t('clientTalentTrain.alerts.viewDetail', { defaultValue: 'Ver detalle' })}
                  <ChevronRight className={`h-3.5 w-3.5 transition-transform ${alertsOpen ? 'rotate-90' : ''}`} />
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {alerts.items.filter((a) => a.severity === 'red').length > 0 && (
                  <Badge className="bg-destructive/15 text-destructive border-destructive/30"
                         data-testid="ctt-alert-expired">
                    {t('clientTalentTrain.alerts.expired', { n: alerts.items.filter((a) => a.severity === 'red').length })}
                  </Badge>
                )}
                {alerts.items.filter((a) => a.severity === 'amber').length > 0 && (
                  <Badge className="bg-warning/15 text-warning border-warning/30"
                         data-testid="ctt-alert-expiring">
                    {t('clientTalentTrain.alerts.expiringSoon', { n: alerts.items.filter((a) => a.severity === 'amber').length })}
                  </Badge>
                )}
              </div>
              {alertsOpen && (
                <div className="mt-3 space-y-1.5" data-testid="ctt-alerts-detail">
                  {alerts.items.map((a) => {
                    const person = users.find((r) => r.user?.id === a.user_id)?.user;
                    const due = a.due_date ? new Date(a.due_date) : null;
                    const days = due ? Math.ceil((due - new Date()) / 86400000) : null;
                    return (
                      <div key={a.action_id}
                           className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs rounded-lg border border-border/60 bg-background/60 px-3 py-2"
                           data-testid={`ctt-alert-item-${a.action_id}`}>
                        <span className="font-semibold">{person?.name || person?.full_name || '—'}</span>
                        <span className="text-muted-foreground">{a.training_name}</span>
                        <span className="tabular-nums">{due ? due.toLocaleDateString(i18n.language?.startsWith('es') ? 'es' : 'en', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                        <Badge className={a.severity === 'red' ? 'bg-destructive/15 text-destructive border-destructive/30' : 'bg-warning/15 text-warning border-warning/30'}>
                          {a.severity === 'red'
                            ? t('clientTalentTrain.alerts.itemExpired', { defaultValue: 'Vencida' })
                            : t('clientTalentTrain.alerts.itemDays', { n: days, defaultValue: '{{n}} días restantes' })}
                        </Badge>
                        {person?.id && (
                          <Button size="sm" variant="outline" className="h-6 text-[11px] ml-auto"
                                  onClick={() => navigate(`/client/talenttrain/${person.id}?focus=${a.action_id}`)}
                                  data-testid={`ctt-alert-open-${a.action_id}`}>
                            {t('clientTalentTrain.alerts.openPlan', { defaultValue: 'Ver plan' })}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={GraduationCap} label={t('clientTalentTrain.kpi.collaborators')}
                 value={totals.withPlan} accent="primary" testid="ctt-kpi-collabs" />
        <KpiCard icon={BookOpen} label={t('clientTalentTrain.kpi.actions')}
                 value={totals.total} accent="info" testid="ctt-kpi-actions" />
        <KpiCard icon={Award} label={t('clientTalentTrain.kpi.completionRate')}
                 value={`${totals.completion}%`} accent="success" testid="ctt-kpi-completion" />
        <KpiCard icon={Activity} label={t('clientTalentTrain.kpi.inProgress')}
                 value={totals.inProgress} accent="warning" testid="ctt-kpi-inprogress" />
      </div>

      {/* Progress gauges + Routes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 shadow-card lg:col-span-1" data-testid="ctt-progress-card">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">{t('clientTalentTrain.progress.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('clientTalentTrain.progress.subtitle')}</p>
          </div>
          {totals.total === 0 ? (
            <GuidedEmptyState
              compact
              dataTestId="tt-progress-empty"
              icon={GraduationCap}
              title={t('guidedEmpty.talenttrain.title')}
              why={t('guidedEmpty.talenttrain.why')}
              steps={[1, 2, 3, 4].map((n) => t(`guidedEmpty.talenttrain.step${n}`))}
            />
          ) : (
            <div className="space-y-4">
              {[
                { label: t('clientTalentTrain.progress.planned'),     val: totals.planned,    color: 'bg-muted-foreground' },
                { label: t('clientTalentTrain.progress.inProgress'),  val: totals.inProgress, color: 'bg-warning' },
                { label: t('clientTalentTrain.progress.completed'),   val: totals.completed,  color: 'bg-success' },
              ].map((row) => {
                const pct = totals.total > 0 ? Math.round((row.val / totals.total) * 100) : 0;
                return (
                  <div key={row.label}>
                    <div className="flex justify-between text-xs font-medium mb-1">
                      <span>{row.label}</span>
                      <span className="tabular-nums">{row.val} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${row.color} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-5 shadow-card lg:col-span-2" data-testid="ctt-routes-card">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">{t('clientTalentTrain.routes.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('clientTalentTrain.routes.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ROUTE_DEFS.map((r) => {
              const Icon = r.icon;
              return (
                <button key={r.code} type="button"
                        onClick={() => setOpenRoute(r.code)}
                        className="border rounded-lg p-4 hover:shadow-md hover:border-primary/40 transition-smooth flex flex-col text-left cursor-pointer"
                        data-testid={`ctt-route-${r.code}`}>
                  <Icon className="h-5 w-5 text-primary mb-2" />
                  <h3 className="font-semibold text-[15px]">{t(`clientTalentTrain.routes.${r.code}`)}</h3>
                  <p className="text-sm text-muted-foreground mt-1 flex-1">
                    {t(`clientTalentTrain.routes.${r.code}Desc`)}
                  </p>
                </button>
              );
            })}
          </div>
          <RouteDetailDialog code={openRoute} onClose={() => setOpenRoute(null)} />
        </Card>
      </div>

      {/* Department coverage */}
      {/* V5.4 §8 — training certificate expiration / recertification alerts */}
      <TtCertAlerts />

      <Card className="p-5 shadow-card" data-testid="ctt-departments-card">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">{t('clientTalentTrain.departments.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('clientTalentTrain.departments.subtitle')}</p>
        </div>
        {byDept.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t('clientTalentTrain.departments.empty')}</p>
        ) : (
          <ul className="space-y-3">
            {byDept.map((d) => (
              <li key={d.dept} data-testid={`ctt-dept-${d.dept}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{d.dept}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {d.withActions}/{d.total} · {d.pct}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${d.pct >= 70 ? 'bg-success' : d.pct >= 40 ? 'bg-warning' : 'bg-destructive'}`}
                    style={{ width: `${d.pct}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Collaborator table */}
      <Card className="p-5 shadow-card" data-testid="ctt-table-card">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t('clientTalentTrain.table.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('clientTalentTrain.table.subtitle')}</p>
          </div>
        </div>
        {sortedUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t('clientTalentTrain.table.empty')}</p>
        ) : (
          <Table className="text-[15px]">
            <TableHeader>
              <TableRow>
                <TableHead>{t('clientTalentTrain.table.th.collab')}</TableHead>
                <TableHead>{t('clientTalentTrain.table.th.position')}</TableHead>
                <TableHead className="text-center">{t('clientTalentTrain.table.th.actions')}</TableHead>
                <TableHead className="text-center">{t('clientTalentTrain.table.th.completed')}</TableHead>
                <TableHead>{t('clientTalentTrain.table.th.progress')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((r, idx) => {
                const a = r.action_counts || {};
                const pct = a.total > 0 ? Math.round(((a.completed || 0) / a.total) * 100) : 0;
                return (
                  <TableRow
                    key={r.user?.id || idx}
                    data-testid={`ctt-row-${idx}`}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(`/client/talenttrain/${r.user?.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">
                          {initials(r.user?.name)}
                        </span>
                        <span className="font-medium">{r.user?.name}</span>
                        {(unreadMap[r.user?.id] || 0) > 0 && (
                          <Badge className="bg-primary text-primary-foreground text-xs px-1.5" data-testid={`tt-unread-badge-${r.user?.id}`}>
                            {unreadMap[r.user?.id]}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.user?.position || '—'}</TableCell>
                    <TableCell className="text-center tabular-nums">{a.total || 0}</TableCell>
                    <TableCell className="text-center text-success font-semibold tabular-nums">
                      {a.completed || 0}
                    </TableCell>
                    <TableCell className="w-48">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-success rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs tabular-nums w-9 text-right">{pct}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ===========================================================================
// Page entry point
// ===========================================================================
export default function ClientTalentTrain() {
  const { t, i18n } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useApp();
  const canEdit = ['client_admin', 'client_hr'].includes(authUser?.role);
  const isEvaluated = authUser?.role === 'evaluated';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evalOpen, setEvalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const detailApi = useMemo(() => ({
    list:    () => clientApi.ttListUsers(),
    getUser: (uid) => clientApi.ttGetUser(uid),
    create:  (uid, body) => clientApi.ttCreateAction(uid, body),
    update:  (aid, patch) => clientApi.ttUpdateAction(aid, patch),
    remove:  (aid) => clientApi.ttDeleteAction(aid),
    discussionGet: (uid) => clientApi.ttDiscussionGet(uid),
    discussionPost: (uid, text) => clientApi.ttDiscussionPost(uid, text),
    discussionRead: (uid) => clientApi.ttDiscussionRead(uid),
    discussionUnread: () => clientApi.ttDiscussionUnread(),
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await clientApi.ttListUsers());
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (!userId) load();
  }, [load, userId]);

  const users = data?.users || [];

  if (userId) {
    return (
      <TalentTrainShell
        portal="client"
        canEdit={canEdit}
        api={detailApi}
        initialUserId={userId}
      />
    );
  }

  if (loading) {
    return (
      <>
        <Topbar title={t('clientTalentTrain.title')} subtitle={t('clientTalentTrain.subtitle')} />
        <div className="py-16 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  // Certificates retired (Founder 2026-08): the evaluated role no longer has a
  // TalentTrain surface here — the plan is managed by company administrators.
  if (isEvaluated) {
    return (
      <>
        <Topbar title={t('clientTalentTrain.title')} subtitle={t('clientTalentTrain.subtitle')} />
        <div className="p-6 lg:p-8">
          <Card className="p-8 text-center text-muted-foreground" data-testid="ctt-evaluated-notice">
            {t('clientTalentTrain.evaluatedNotice')}
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('clientTalentTrain.title')}
            <Badge className="bg-primary/15 text-primary border-primary/30">
              {t('suiteChip.talenttrain')}
            </Badge>
          </span>
        )}
        subtitle={t('clientTalentTrain.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <RequestHMSupport suite="talenttrain" origin="/client/talenttrain" />
            {canEdit && (
              <Button size="sm" variant="outline" data-testid="ctt-new-route"
                      onClick={() => setEvalOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> {t('clientTalentTrain.actions.newRoute')}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" data-testid="ctt-import-excel"
                      onClick={() => setImportOpen(true)}>
                <GraduationCap className="h-4 w-4 mr-1.5" /> {i18n.language?.startsWith('es') ? 'Importar Excel' : 'Import Excel'}
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid="ctt-export"
                    onClick={() => window.open(clientApi.ttPlanPdfUrl(i18n.language), '_blank', 'noopener,noreferrer')}>
              <Download className="h-4 w-4 mr-1.5" /> {t('clientTalentTrain.actions.exportPlan')}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-5" data-testid="client-tt-page">
        <SuiteHero suite="talenttrain" icon={GraduationCap} />
        <Tabs defaultValue="summary" className="w-full">
          <TabsList data-testid="ctt-tabs">
            <TabsTrigger value="summary" data-testid="ctt-tab-summary">
              {t('clientTalentTrain.tabs.summary')}
            </TabsTrigger>
            <TabsTrigger value="matrix" data-testid="ctt-tab-matrix">
              {t('clientTalentTrain.tabs.matrix')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" data-testid="ctt-panel-summary" className="mt-4">
            <SummaryTab users={users} navigate={navigate} canEdit={canEdit} />
          </TabsContent>

          <TabsContent value="matrix" data-testid="ctt-panel-matrix" className="mt-4">
            <MatrixTab canEdit={canEdit} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Client Autonomy (founder remediation 2026-06) — the primary action is
           SELF-SERVICE: pick the collaborator and manage their existing training
           plan (create actions, resources, follow-up). CTH support stays optional. */}
      <Dialog open={evalOpen} onOpenChange={setEvalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ctt-new-action-dialog">
          <DialogHeader>
            <DialogTitle>{t('clientTalentTrain.actions.newRoute')}</DialogTitle>
            <DialogDescription>
              {t('clientTalentTrain.actions.newRouteDesc', { defaultValue: 'Selecciona al colaborador para gestionar su plan de formación: crea acciones, define objetivos, agrega recursos y da seguimiento — sin necesidad de aprobación de CTH.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto divide-y rounded-md border" data-testid="ctt-new-action-list">
            {users.map((r, idx) => (
              <button
                key={r.user?.id || idx}
                type="button"
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm hover:bg-muted/40 text-left"
                data-testid={`ctt-pick-${r.user?.id}`}
                onClick={() => { setEvalOpen(false); navigate(`/client/talenttrain/${r.user?.id}`); }}
              >
                <span className="flex flex-col min-w-0 text-left">
                  <span className="font-medium truncate" title={r.user?.name}>{r.user?.name}</span>
                  <span className="text-xs text-muted-foreground whitespace-normal" title={r.user?.position || ''}>{r.user?.position || ''}</span>
                </span>
              </button>
            ))}
            {!users.length && (
              <p className="px-3 py-3 text-xs text-muted-foreground italic">
                {t('clientTalentTrain.actions.noCollaborators', { defaultValue: 'No hay colaboradores disponibles.' })}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <SuiteImportWizard suite="talenttrain" open={importOpen}
                         onClose={() => setImportOpen(false)} onImported={load} />
    </>
  );
}
